import { handleActions } from 'redux-actions';

import { getLogger } from '../../utils/core';

import { createDialog, removeDialog, showNextDialog, updateDialogProps } from './rootUiActions';
import { mkRootUiStore } from './rootUiTypes';

const log = getLogger('flux/root-ui actions');

let nextId = 1;

const findById = (id) => (object) => object.id === id;

/**
 * This reducer changes the visibility of a dialog in the store by ID. If no ID is provided,
 * the dialog on the top of the stack is updated.
 */
function removeDialogWithAnimation(state, dialogId) {
  log.info(`dismiss dialog id=${dialogId}`);

  // when removing, optimize primary use case of popping last dialog pushed
  const activeModal = state.activeModals.last();
  if (activeModal != null && (dialogId === 0 || activeModal.id === dialogId)) {
    return state.setIn(['activeModals', -1, 'visible'], false);
  }

  const predicate = findById(dialogId);

  // not really sure if this is a valid use-case or not...
  let index = state.activeModals.findIndex(predicate);
  if (index >= 0) {
    return state.setIn(['activeModals', index, 'visible'], false);
  }

  index = state.queuedModals.findIndex(predicate);
  if (index >= 0) {
    return state.setIn(['queuedModals', index, 'visible'], false);
  }

  log.warn('Tried to dismiss non-existing dialog', dialogId);
  return state;
}

/**
 * This reducer removes a dialog from the store by ID. If no ID is provided,
 * the dialog on the top of the stack is removed.
 */
function removeDialogWithoutAnimation(state, dialogId) {
  log.info(`remove dialog id=${dialogId}`);

  // when removing, optimize primary use case of popping last dialog pushed
  const activeModal = state.activeModals.last();
  if (activeModal != null && (dialogId === 0 || activeModal.id === dialogId)) {
    return state.update('activeModals', (modals) => modals.pop());
  }

  // not really sure if this is a valid use-case or not...
  const predicate = findById(dialogId);
  let index = state.activeModals.findIndex(predicate);
  if (index >= 0) {
    return state.update('activeModals', (modals) => modals.splice(index, 1));
  }

  index = state.queuedModals.findIndex(predicate);
  if (index >= 0) {
    return state.update('queuedModals', (modals) => modals.splice(index, 1));
  }

  log.warn('Tried to remove non-existing dialog', dialogId);
  return state;
}

/* eslint-disable no-param-reassign */
export const reducer = handleActions(
  {
    [createDialog]: (state, { payload: uiData }) => {
      log.info('creating dialog', uiData, uiData.type, uiData.id, uiData.props);
      let uiDataWithId = uiData;
      if (!uiData.id || uiData.id === '0') {
        uiDataWithId = uiData.merge({ id: `ROOT_DIALOG_${nextId}` });
        nextId += 1;
      } else {
        // do not process if a dialog with the same ID is already on the active or queued stacks
        if (
          // eslint-disable-line no-lonely-if
          state.activeModals.findIndex((x) => x.id === uiDataWithId.id) !== -1 ||
          state.queuedModals.findIndex((x) => x.id === uiDataWithId.id) !== -1
        ) {
          // log.debug('Dialog is already displayed or queued', uiDataWithId);
          return state;
        }
      }
      // log.debug('Done with check...');

      if (state.activeModals.size === 0 || uiData.allowNesting) {
        return state.update('activeModals', (modals) => modals.push(uiDataWithId));
      }
      return state.update('queuedModals', (modals) => modals.push(uiDataWithId));
    },

    [removeDialog]: (state, { payload: dialogId = 0, meta: { animate = false } = {} }) => {
      if (animate) {
        return removeDialogWithAnimation(state, dialogId);
      }

      return removeDialogWithoutAnimation(state, dialogId);
    },
    [showNextDialog]: (state) => {
      log.info('Show next dialog if available.');
      // if we have no more active-modals, and we have a queued modal, then move first queued modal to active modals list
      if (state.activeModals.size === 0) {
        const queuedModal = state.queuedModals.get(0);
        if (queuedModal != null) {
          return state.merge({
            activeModals: state.activeModals.push(queuedModal),
            queuedModals: state.queuedModals.delete(0),
          });
        }
      }

      return state;
    },
    [updateDialogProps]: (state, { payload }) => {
      const { id, props } = payload;

      // check active list first
      const indexInActiveModals = state.activeModals.findIndex((x) => x.id === id);
      if (indexInActiveModals !== -1) {
        return state.set(
          'activeModals',
          state.activeModals.update(indexInActiveModals, (modal) => modal.mergeIn(['props'], props)),
        );
      }

      // check queued list if we didn't find one in active list
      const indexInQueuedModals = state.queuedModals.findIndex((x) => x.id === id);
      if (indexInQueuedModals !== -1) {
        return state.set(
          'queuedModals',
          state.queuedModals.update(indexInQueuedModals, (modal) => modal.mergeIn(['props'], props)),
        );
      }

      // do nothing if we couldn't find dialog
      return state;
    },
  },
  mkRootUiStore(),
);

export const REDUCER_KEY = 'rootUiStore';
export default reducer;
